/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { ExpExpressionDefinitionFactory } from "abweb/generated/abwebinternal/api/dto/ExpExpressionDefinition";
import { ExpExpressionNodeNodeTypeEnum } from "abweb/generated/abwebinternal/api/dto/ExpExpressionNode";
import { Assert } from "abweb/utils/Assert";
import { AbMaybePromise } from "abweb/utils/async/AbMaybePromise";
import { ObjectUtils } from "abweb/utils/ObjectUtils";
import { StringUtils } from "abweb/utils/StringUtils";
export let Expression;
(function (_Expression) {
  let Domain;
  (function (_Domain) {
    let Constants;
    (function (_Constants) {
      const OR_OPERATOR_ID = _Constants.OR_OPERATOR_ID = "OR";
      const AND_OPERATOR_ID = _Constants.AND_OPERATOR_ID = "AND";
      const JOIN_OPERATOR_CATEGORY_ID = _Constants.JOIN_OPERATOR_CATEGORY_ID = "condition";
      const VALUE_TYPE_STRING = _Constants.VALUE_TYPE_STRING = "string";
      const VALUE_TYPE_NUMBER = _Constants.VALUE_TYPE_NUMBER = "number";
      const VALUE_TYPE_INTEGER = _Constants.VALUE_TYPE_INTEGER = "integer";
      const VALUE_TYPE_BOOLEAN = _Constants.VALUE_TYPE_BOOLEAN = "boolean";
      const VALUE_TYPE_DATE = _Constants.VALUE_TYPE_DATE = "date";
      const VALUE_TYPE_DATETIME = _Constants.VALUE_TYPE_DATETIME = "datetime";
      const VALUE_TYPE_REFERENCE = _Constants.VALUE_TYPE_REFERENCE = "reference";
      const VALUE_TYPE_WILDCARD_ANY = _Constants.VALUE_TYPE_WILDCARD_ANY = "any";
      const CATEGORY_ITERATION = _Constants.CATEGORY_ITERATION = "iteration";
      const CATEGORY_LITERAL = _Constants.CATEGORY_LITERAL = "literal";
      const CATEGORY_REFERENCE = _Constants.CATEGORY_REFERENCE = "reference";
      const CATEGORY_SUB_EXPRESSION = _Constants.CATEGORY_SUB_EXPRESSION = "sub_expression";
      const CATEGORY_FORMULA = _Constants.CATEGORY_FORMULA = "formula";
      const PSEUDO_CATEGORY_FUNCTION = _Constants.PSEUDO_CATEGORY_FUNCTION = "function";
      const PSEUDO_CATEGORY_FUNCTION_LABEL = _Constants.PSEUDO_CATEGORY_FUNCTION_LABEL = "f(x)";
      const ROLE_CONDITION = _Constants.ROLE_CONDITION = "CONDITION";
      const ROLE_ACTION = _Constants.ROLE_ACTION = "ACTION";
      const ROLE_ABSENT = _Constants.ROLE_ABSENT = "ABSENT";
      const ROLE_FUNCTION = _Constants.ROLE_FUNCTION = "FUNCTION";
      const ROLE_JOIN = _Constants.ROLE_JOIN = "JOIN";
      const ROLE_FORMULA = _Constants.ROLE_FORMULA = "FORMULA";
      const FUNCTION_SUMMARY_STANDARD = _Constants.FUNCTION_SUMMARY_STANDARD = "standard";
      const DEFAULT_SOURCE_VALUE_OPERAND_NAME = _Constants.DEFAULT_SOURCE_VALUE_OPERAND_NAME = "source";
      const SINGLE_FIRE_ACTION_LIST_EXECUTION_FLAG = _Constants.SINGLE_FIRE_ACTION_LIST_EXECUTION_FLAG = "single-fire-action-list";
      const ADDITIONAL_DATA_COMPLETE_OVERRIDE = _Constants.ADDITIONAL_DATA_COMPLETE_OVERRIDE = "gee-node-complete-override";
    })(Constants || (Constants = _Domain.Constants || (_Domain.Constants = {})));
    let OperatorRole;
    (function (_OperatorRole) {
      function isRowBased(operatorRoleID) {
        switch (operatorRoleID) {
          case Constants.ROLE_CONDITION:
          case Constants.ROLE_ACTION:
            return true;
          default:
            return false;
        }
      }
      _OperatorRole.isRowBased = isRowBased;
      function isJoin(expOperator) {
        return expOperator.roleID === Constants.ROLE_JOIN;
      }
      _OperatorRole.isJoin = isJoin;
      function isCondition(expOperator) {
        return expOperator.roleID === Constants.ROLE_CONDITION;
      }
      _OperatorRole.isCondition = isCondition;
      function isFormula(expOperator) {
        return expOperator.roleID === Constants.ROLE_FORMULA;
      }
      _OperatorRole.isFormula = isFormula;
      function isAbsent(expOperator) {
        return expOperator.roleID === Constants.ROLE_ABSENT;
      }
      _OperatorRole.isAbsent = isAbsent;
      function getID(expExpressionNodeNodeType) {
        switch (expExpressionNodeNodeType) {
          case ExpExpressionNodeNodeTypeEnum.OPERATOR:
            return Constants.ROLE_CONDITION;
          case ExpExpressionNodeNodeTypeEnum.ACTION_LIST:
            return Constants.ROLE_ACTION;
          default:
            Assert.unexpected();
        }
      }
      _OperatorRole.getID = getID;
    })(OperatorRole || (OperatorRole = _Domain.OperatorRole || (_Domain.OperatorRole = {})));
    let Operator;
    (function (_Operator) {
      function getDisplayNameForSelectedValue(expOperator) {
        return getDisplayName(expOperator, false);
      }
      _Operator.getDisplayNameForSelectedValue = getDisplayNameForSelectedValue;
      function getDisplayNameForDropdownItem(expOperator) {
        return getDisplayName(expOperator, true);
      }
      _Operator.getDisplayNameForDropdownItem = getDisplayNameForDropdownItem;
      function getDisplayName(expOperator, preferSelectorLabel) {
        let displayName;
        if (preferSelectorLabel) {
          displayName = expOperator.selectorLabel;
        }
        displayName ??= expOperator.label;
        if (expOperator.deprecated) {
          displayName = AbReactResources.expression_operator_deprecated(displayName);
        }
        return displayName;
      }
      function isMultiple(expOperator) {
        const {
          argumentOperands: expArgumentOperands
        } = expOperator;
        return expArgumentOperands?.length === 1 && (expArgumentOperands[0].multiple ?? false);
      }
      _Operator.isMultiple = isMultiple;
      function compare(a, b) {
        return a.sortPriority == null && b.sortPriority == null ? a.label.localeCompare(b.label) : (b.sortPriority ?? Number.MIN_VALUE) - (a.sortPriority ?? Number.MIN_VALUE);
      }
      _Operator.compare = compare;
    })(Operator || (Operator = _Domain.Operator || (_Domain.Operator = {})));
    let OperatorGroup;
    (function (_OperatorGroup) {
      function compare(a, b) {
        return b.sortPriority - a.sortPriority;
      }
      _OperatorGroup.compare = compare;
    })(OperatorGroup || (OperatorGroup = _Domain.OperatorGroup || (_Domain.OperatorGroup = {})));
    /**
     * WARNING: This is not an alias or enrichment of ExpArgumentOperand, but represents a different concept.
     * An ArgumentOperand represents an argument to be filled in via the editor. This can be made up of
     * a single ExpArgumentOperand, or a group of ExpArgumentOperands, as per the domain.
     */
    let ArgumentOperand;
    (function (_ArgumentOperand) {
      let Identity;
      (function (_Identity) {
        function compare(a, b) {
          let result;
          if (a.restrictToFunctionInput) {
            result = 1;
          } else if (b.restrictToFunctionInput && !a.restrictToFunctionInput) {
            result = -1;
          } else {
            const sortPriorityA = a.expValueCategory.sortPriority != null ? a.expValueCategory.sortPriority : 0;
            const sortPriorityB = b.expValueCategory.sortPriority != null ? b.expValueCategory.sortPriority : 0;
            result = sortPriorityB - sortPriorityA;
            if (result === 0 && a.valueCategoryID === b.valueCategoryID) {
              result = StringUtils.compareStrings(a.label || "", b.label || "");
            }
          }
          return result;
        }
        _Identity.compare = compare;
      })(Identity || (Identity = _ArgumentOperand.Identity || (_ArgumentOperand.Identity = {})));
    })(ArgumentOperand || (ArgumentOperand = _Domain.ArgumentOperand || (_Domain.ArgumentOperand = {})));
    /**
     * A condition comprises various elements, such as source value an operator and any number of argument operands.
     * This interface defines the choices that the expression domain model identifies given the status of the other
     * elements of the condition. For instance, which operators are applicable given the source value type and category.
     */
    let ElementOption;
    (function (_ElementOption) {
      let Type = /*#__PURE__*/function (Type) {
        Type["OPERATOR"] = "OPERATOR";
        Type["VALUE"] = "VALUE";
        Type["FORMULA"] = "FORMULA";
        return Type;
      }({});
      _ElementOption.Type = Type;
    })(ElementOption || (ElementOption = _Domain.ElementOption || (_Domain.ElementOption = {})));
  })(Domain || (Domain = _Expression.Domain || (_Expression.Domain = {})));
  let Definition;
  (function (_Definition) {
    let ImportVisitor;
    (function (_ImportVisitor) {
      function compose(a, b) {
        return {
          finalizeRootExpExpressionNode(expExpressionNode) {
            return AbMaybePromise.map(a.finalizeRootExpExpressionNode?.(expExpressionNode), () => b.finalizeRootExpExpressionNode?.(expExpressionNode));
          },
          finalizeExpExpressionDefinition(expExpressionDefinition) {
            return AbMaybePromise.map(a.finalizeExpExpressionDefinition?.(expExpressionDefinition), () => b.finalizeExpExpressionDefinition?.(expExpressionDefinition));
          }
        };
      }
      _ImportVisitor.compose = compose;
      function composeNullable(a, b) {
        if (a != null && b != null) {
          return compose(a, b);
        }
        if (a != null) {
          return a;
        }
        if (b != null) {
          return b;
        }
        return undefined;
      }
      _ImportVisitor.composeNullable = composeNullable;
      function composeAll(importVisitor, ...importVisitors) {
        return importVisitors.reduce((acc, importVisitor) => compose(acc, importVisitor), importVisitor);
      }
      _ImportVisitor.composeAll = composeAll;
      function composeAllNullable(importVisitor, ...importVisitors) {
        return importVisitors.reduce((acc, importVisitor) => composeNullable(acc, importVisitor), importVisitor);
      }
      _ImportVisitor.composeAllNullable = composeAllNullable;
    })(ImportVisitor || (ImportVisitor = _Definition.ImportVisitor || (_Definition.ImportVisitor = {})));
    let ExportVisitor;
    (function (_ExportVisitor) {
      function noOp() {
        return {};
      }
      _ExportVisitor.noOp = noOp;
      function compose(a, b) {
        return {
          startJoinExpExpressionNode(expExpressionNode) {
            return AbMaybePromise.map(a.startJoinExpExpressionNode?.(expExpressionNode), () => b.startJoinExpExpressionNode?.(expExpressionNode));
          },
          visitRowExpExpressionNode(expExpressionNode, rowModel) {
            return AbMaybePromise.map(a.visitRowExpExpressionNode?.(expExpressionNode, rowModel), () => b.visitRowExpExpressionNode?.(expExpressionNode, rowModel));
          },
          endJoinExpExpressionNode(expExpressionNode) {
            return AbMaybePromise.map(a.endJoinExpExpressionNode?.(expExpressionNode), () => b.endJoinExpExpressionNode?.(expExpressionNode));
          },
          finalizeRootExpExpressionNode(expExpressionNode) {
            return AbMaybePromise.map(a.finalizeRootExpExpressionNode?.(expExpressionNode), () => b.finalizeRootExpExpressionNode?.(expExpressionNode));
          },
          finalizeExpExpressionDefinition(expExpressionDefinition, model) {
            return AbMaybePromise.map(a.finalizeExpExpressionDefinition?.(expExpressionDefinition, model), () => b.finalizeExpExpressionDefinition?.(expExpressionDefinition, model));
          }
        };
      }
      _ExportVisitor.compose = compose;
      function composeNullable(a, b) {
        if (a != null && b != null) {
          return compose(a, b);
        }
        if (a != null) {
          return a;
        }
        if (b != null) {
          return b;
        }
        return undefined;
      }
      _ExportVisitor.composeNullable = composeNullable;
      function composeAll(exportVisitor, ...exportVisitors) {
        return exportVisitors.reduce((acc, exportVisitor) => compose(acc, exportVisitor), exportVisitor);
      }
      _ExportVisitor.composeAll = composeAll;
      function composeAllNullable(exportVisitor, ...exportVisitors) {
        return exportVisitors.reduce((acc, exportVisitor) => composeNullable(acc, exportVisitor), exportVisitor);
      }
      _ExportVisitor.composeAllNullable = composeAllNullable;
    })(ExportVisitor || (ExportVisitor = _Definition.ExportVisitor || (_Definition.ExportVisitor = {})));
    function parseToMutable(expExpressionDefinitionJson) {
      return ExpExpressionDefinitionFactory.FromJSON(JSON.parse(expExpressionDefinitionJson));
    }
    _Definition.parseToMutable = parseToMutable;
    function parse(expExpressionDefinitionJson) {
      const expExpressionDefinition = parseToMutable(expExpressionDefinitionJson);
      return ObjectUtils.deepFreeze(expExpressionDefinition);
    }
    _Definition.parse = parse;
    function maybeParse(maybeExpExpressionDefinitionJson) {
      if (maybeExpExpressionDefinitionJson != null) {
        return parse(maybeExpExpressionDefinitionJson);
      }
    }
    _Definition.maybeParse = maybeParse;
    function serialize(expExpressionDefinition, space = 2) {
      return JSON.stringify(expExpressionDefinition, null, space);
    }
    _Definition.serialize = serialize;
    function maybeSerialize(maybeExpExpressionDefinition) {
      if (maybeExpExpressionDefinition != null) {
        return serialize(maybeExpExpressionDefinition);
      }
    }
    _Definition.maybeSerialize = maybeSerialize;
    function equalsInternal(baselineExpExpressionDefinition, currentExpExpressionDefinition, deepCompareReplacer) {
      return ObjectUtils.deepCompare(ObjectUtils.deepCopy(baselineExpExpressionDefinition, deepCompareReplacer), ObjectUtils.deepCopy(currentExpExpressionDefinition, deepCompareReplacer));
    }
    function equals(baselineExpExpressionDefinition, currentExpExpressionDefinition) {
      return equalsInternal(baselineExpExpressionDefinition, currentExpExpressionDefinition);
    }
    _Definition.equals = equals;
    function fuzzyEquals(baselineExpExpressionDefinition, currentExpExpressionDefinition, deepCompareReplacer = Definition.defaultDeepCompareReplacer) {
      return equalsInternal(baselineExpExpressionDefinition, currentExpExpressionDefinition, deepCompareReplacer);
    }
    _Definition.fuzzyEquals = fuzzyEquals;
    function defaultDeepCompareReplacer(key, value) {
      switch (key) {
        case "operandValues":
          if (value != null && value.length === 0) {
            return undefined;
          }
          break;
        case "isComplete":
        case "multiple":
        case "disabled":
          if (value === false) {
            return undefined;
          }
          break;
        case "editable":
          if (value === true) {
            return undefined;
          }
          break;
      }
      return value;
    }
    _Definition.defaultDeepCompareReplacer = defaultDeepCompareReplacer;
    function isBooleanDefinition(expExpressionDefinition) {
      return isRowBasedDefinition(expExpressionDefinition) && expExpressionDefinition.rootNode.nodeType === ExpExpressionNodeNodeTypeEnum.OPERATOR;
    }
    _Definition.isBooleanDefinition = isBooleanDefinition;
    function isActionDefinition(expExpressionDefinition) {
      return isRowBasedDefinition(expExpressionDefinition) && expExpressionDefinition.rootNode.nodeType === ExpExpressionNodeNodeTypeEnum.ACTION_LIST;
    }
    _Definition.isActionDefinition = isActionDefinition;
    function isRowBasedDefinition(expExpressionDefinition) {
      return !isNonBooleanDefinition(expExpressionDefinition);
    }
    _Definition.isRowBasedDefinition = isRowBasedDefinition;
    function isNonBooleanDefinition(expExpressionDefinition) {
      return expExpressionDefinition.output != null;
    }
    _Definition.isNonBooleanDefinition = isNonBooleanDefinition;
  })(Definition || (Definition = _Expression.Definition || (_Expression.Definition = {})));
  let Node;
  (function (_Node) {
    let Producer;
    (function (_Producer) {
      function equals(a, b) {
        return ObjectUtils.deepCompare(a?.toExpExpressionNode(), b?.toExpExpressionNode());
      }
      _Producer.equals = equals;
      function mapNullable(...producers) {
        return producers.filter(producer => producer != null).map(producer => producer.toExpExpressionNode());
      }
      _Producer.mapNullable = mapNullable;
    })(Producer || (Producer = _Node.Producer || (_Node.Producer = {})));
  })(Node || (Node = _Expression.Node || (_Expression.Node = {})));
})(Expression || (Expression = {}));
//# sourceMappingURL=Expression.js.map
